import React, { useState, useContext } from "react";
import { Card, Col, Row, Table, Form, Modal, Button, Spinner, ButtonGroup, ToggleButton} from "react-bootstrap";
import { Helmet } from 'react-helmet-async';
import NotyfContext from "../../contexts/NotyfContext";
import { UserSettingsContext } from "../../contexts/UserSettingsContext";
import authenticatedFetch from "../../utils/fetch"
import LastRun from "../../utils/LastRun";
import EditableExpires from "../../utils/EditableExpires";
import InputMask from "react-input-mask";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";


const OpenTasksModal = ({ show, onClose, runSuccess}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [includeRecentlyCompleted, setIncludeRecentlyCompleted] = useState(false);
  const [completedDays, setCompletedDays] = useState("");

  const notyf = useContext(NotyfContext);

  const handleExportClicked = (e) => {
      e.preventDefault();
      setIsExporting(true);
  
      authenticatedFetch({
        method: "POST",
        path:  "report/opentasks",
        requestData: {
          includeRecentlyCompleted: includeRecentlyCompleted,
          completedDays: includeRecentlyCompleted ? completedDays.replace(/[^\d]/g, '') : 0,
        },
        successHandler: function(result) {
          window.open(result.url);
          setIsExporting(false);

          runSuccess("opentasks")
          onClose();
        },
        errorHandler: function(error) {
          setIsExporting(false);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
  }
  
  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Configure Report | Open Tasks</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                <td><Form.Check type="checkbox" inline name="group1" label="Include Recently Completed Tasks" checked={includeRecentlyCompleted} onChange={(e) => {setIncludeRecentlyCompleted(e.target.checked)}} /></td>
                {includeRecentlyCompleted ?
                  <td>
                    <InputMask
                      mask="99"
                      value={completedDays}
                      placeholder="Completed in Past Days..."
                      onChange={(e) => {setCompletedDays(e.target.value)}} >
                      {(inputProps) => <Form.Control {...inputProps} type="text"/>}
                    </InputMask>
                  </td>
                :
                  <td>&nbsp;</td>
                }
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '15px', marginTop: '11px', marginBottom: '11px'}}/>
          :
          <>
              <Button onClick={onClose}>Close</Button>
              <Button onClick={(e) => {handleExportClicked(e)}}>Run Report</Button>
          </>
          }
        </Modal.Footer>
      </Modal>
  );
}


const CompletedTasksModal = ({ show, onClose, runSuccess}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [searchByOption, setSearchByOption] = useState("createdDate");

  const notyf = useContext(NotyfContext);

  const handleFromDateChanged = (value) => {
      setFromDate(value);
  }

  const handleToDateChanged = (value) => {
      setToDate(value);
  }


  const handleExportClicked = (e) => {
      e.preventDefault();
      setIsExporting(true);
  
      authenticatedFetch({
        method: "POST",
        path:  "report/completedtasks",
        requestData: {
          searchByOption: searchByOption,
          fromDate: fromDate,
          toDate: toDate
        },
        successHandler: function(result) {
          window.open(result.url);
          setIsExporting(false);

          runSuccess("completedtasks")
          onClose();
        },
        errorHandler: function(error) {
          setIsExporting(false);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
  }
  
  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Configure Report | Completed Task Activity</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>Search By:</td>
                  <td>
                    <Form.Select
                      style={{height: "40px"}}
                      value={searchByOption}
                      onChange={(e) => {setSearchByOption(e.target.value);}}
                      >
                        <option value="createdDate">Created Date</option>
                        <option value="completedDate">Completed Date</option>
                  </Form.Select>
                  </td>
              </tr>
              <tr>
                <td>Date Range:</td>
                <td>
                  <Form.Label style={{display: "inline"}}><EditableExpires display="inline" size="lg" width="202px" warnWithColors={false} localExpires={fromDate} localActive={true} editingExpires={true} handleExpiresChanged={handleFromDateChanged} /></Form.Label>
                  &nbsp;&nbsp;&nbsp;to&nbsp;&nbsp;&nbsp;
                  <Form.Label style={{display: "inline"}}><EditableExpires display="inline" size="lg" width="202px" warnWithColors={false} localExpires={toDate} localActive={true} editingExpires={true} handleExpiresChanged={handleToDateChanged} /></Form.Label>
                </td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '15px', marginTop: '11px', marginBottom: '11px'}}/>
          :
          <>
              <Button onClick={onClose}>Close</Button>
              <Button onClick={(e) => {handleExportClicked(e)}}>Run Report</Button>
          </>
          }
        </Modal.Footer>
      </Modal>
  );
}

const getLastMonth = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().slice(0, 7);
};

const TaskResolutionModal = ({ show, onClose, runSuccess}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [month, setMonth] = useState(getLastMonth());
  const [aggregateBy, setAggregateBy] = useState("ALL");

  const notyf = useContext(NotyfContext);

  const handleMonthChanged = (value) => {
      setMonth(value);
  }

  const handleAggregateByChanged = (value) => {
      setAggregateBy(value);
  }

  const handleExportClicked = (e) => {
      e.preventDefault();

      if (!month)      
      {
        notyf.open({type: "error", message: "Please select a month for the report", duration: 5000, dismissable: true, ripple: true});
        return;
      }

      setIsExporting(true);
  
      authenticatedFetch({
        method: "POST",
        path:  "report/taskresolution",
        requestData: {
          month: month,
          aggregateBy: aggregateBy
        },
        successHandler: function(result) {
          if (!result.reports || result.reports.length === 0) 
          {
            notyf.open({type: "warning", message: "No activity available for the selected month", duration: 5000, dismissable: true, ripple: true});
          }
          else
          {
            for (var i=0; i<result.reports.length; i++)
            {
              window.open(result.reports[i].url);
            }
          }
          setIsExporting(false);

          runSuccess("taskresolution")
          onClose();
        },
        errorHandler: function(error) {
          setIsExporting(false);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
  }
  
  const currentMonth = new Date().toISOString().slice(0, 7);

  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Configure Report | Task Resolution</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                <td>Tasks Completed in:</td>
                <td>
                  <Form.Label style={{display: "inline"}}>
                    <Form.Control type="month" max={currentMonth} value={month} onChange={(e) => {handleMonthChanged(e.target.value)}} style={{height: "40px"}} />
                  </Form.Label>
                </td>
              </tr>
              <tr>
                <td>
                  Aggregate by:
                </td>
                <td>
                  <ButtonGroup className="me-auto">
                    <ToggleButton
                        key="ALL"
                        id="ALL"
                        type="radio"
                        variant="outline-primary"
                        style={{color: aggregateBy === "ALL" ? "white" : ""}}
                        value="ALL"
                        checked={aggregateBy === "ALL"}
                        onChange={(e) => {handleAggregateByChanged(e.target.value)}}
                    >All Practices</ToggleButton>
                    <ToggleButton
                        key="PDF"
                        id="PDF"
                        type="radio"
                        variant="outline-primary"
                        style={{color: aggregateBy === "GROUPING" ? "white" : ""}}
                        value="GROUPING"
                        checked={aggregateBy === "GROUPING"}
                        onChange={(e) => {handleAggregateByChanged(e.target.value)}}
                    >Practice Grouping</ToggleButton>
                </ButtonGroup>
                </td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '15px', marginTop: '11px', marginBottom: '11px'}}/>
          :
          <>
              <Button onClick={onClose}>Close</Button>
              <Button onClick={(e) => {handleExportClicked(e)}}>Run Report</Button>
          </>
          }
        </Modal.Footer>
      </Modal>
  );
}

const ReportList = () => {
    const [showCompletedTasksModal, setShowCompletedTasksModal] = useState(false);
    const [showOpenTasksModal, setShowOpenTasksModal] = useState(false);
    const [showTaskResolutionModal, setShowTaskResolutionModal] = useState(false);

    const [lastRunRefresh, setLastRunRefresh] = useState(0);

    const { isH3Admin, authorities } = useContext(UserSettingsContext);

    const runSuccess = (e) => {
      setLastRunRefresh(lastRunRefresh + 1);
    }

    return (
      <Card>
        <Card.Header style={{marginBottom: '-15px'}}>
            <Card.Title>
            Task Reports
            </Card.Title>
          </Card.Header>
        <Table striped hover>
          <thead>
            <tr>
              <td>Report Type</td>
              <td>Available Formats</td>
              <td>Last Run</td>
            </tr>
          </thead>
          <tbody>
            {(isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")) ?
            <tr style={{cursor:"pointer"}} onClick={(e) => {setShowOpenTasksModal(true)}}>
                <td><a href="#">Open Tasks</a></td>
                <td><FontAwesomeIcon icon={faFileCsv} size="lg" style={{color: '#2B7A78'}}/></td>
                <td><LastRun reportType="opentasks" refresh={lastRunRefresh}/></td>
            </tr>
             : <></>
            }
            {(isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")) ?
            <tr style={{cursor:"pointer"}} onClick={(e) => {setShowCompletedTasksModal(true)}}>
                <td><a href="#">Completed Task Activity</a></td>
                <td><FontAwesomeIcon icon={faFileCsv} size="lg" style={{color: '#2B7A78'}}/></td>
                <td><LastRun reportType="completedtasks" refresh={lastRunRefresh}/></td>
            </tr>
             : <></>
            }
            {(isH3Admin) ?
            <tr style={{cursor:"pointer"}} onClick={(e) => {setShowTaskResolutionModal(true)}}>
                <td><a href="#">Task Resolution</a></td>
                <td><FontAwesomeIcon icon={faFilePdf} size="lg" style={{color: '#2B7A78'}}/></td>
                <td><LastRun reportType="taskresolution" refresh={lastRunRefresh}/></td>
            </tr>
             : <></>
            }
          </tbody>
        </Table>
        <OpenTasksModal show={showOpenTasksModal} onClose={() => setShowOpenTasksModal(false)} runSuccess={runSuccess} />
        <CompletedTasksModal show={showCompletedTasksModal} onClose={() => setShowCompletedTasksModal(false)} runSuccess={runSuccess} />
        <TaskResolutionModal show={showTaskResolutionModal} onClose={() => setShowTaskResolutionModal(false)} runSuccess={runSuccess} />
      </Card>
    )
  };

const TaskReports = () => {

    return (
      <Row>
        <Helmet title="Task Reports"/>
        <Col lg="12">
          <ReportList />
        </Col>
      </Row>
    )
  };
  
  export default TaskReports;
  